import React, { useEffect, useState } from 'react';
import { array, bool, func, object } from 'prop-types';

import clsx from 'clsx';

import { Grid, getSelectedState, GridColumn as Column } from '@progress/kendo-react-grid';
import { getter } from '@progress/kendo-react-common';
import { orderBy, filterBy } from '@progress/kendo-data-query';
import { cloneDeep } from 'lodash';

import ProfilesTableRowMenu from './ProfilesTableRowMenu';
import { kendoGridInlineStyling } from '../../helpers/kendoGridInlineStyleHelper';
import classes from './ProfilesTable.module.css';

const idGetter = getter('id');

const initalDataState = { 
    filter: [], 
    sort:   [], 
    take:   20, 
    skip:   0 
};
  
const ProfilesTable = ({
    companyInfo,
    isFullProfileAllowed,
    onSort,
    order,
    onChangeTrendModal,
    onDeleteModal,
    onItemChange,
    onItemToggle,
    profiles,
    updateSelectedState,
    workbookModeEnabled,
    ...props
}) => {
    const [objects, setObjects] = useState([]);
    const [filteredObjects, setFilteredObjects] = useState([]);
    const [dataState, setDataState] = useState(initalDataState);
    const [gridData, setGridData] = useState([]);
    const [tableSelectState, setTableSelectState] = useState({});

    useEffect(() => {
        if (profiles) initilizeTable();
    }, [profiles]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            setDataState(initalDataState);
            initilizeTable();
        }
    }, [workbookModeEnabled]);

    const initilizeTable = () => {
        setObjects(profiles);

        const data = [...profiles];
        const initialData = data.splice(0, 20);

        setFilteredObjects(data);
        setGridData(initialData);
    };

    const actionsCell = props => {
        return (
            <td className={clsx(props.className, classes.ActionsCell)} style={props.style}>
                <ProfilesTableRowMenu
                    companyInfo={companyInfo}
                    hidden={!isFullProfileAllowed}
                    onDeleteModal={onDeleteModal}
                    onTrendIntervalChange={onChangeTrendModal}
                    profile={props.dataItem}
                />
            </td>
        );
    };

    const scrollHandler = (event) => {
        const e = event.nativeEvent;

        if (
            e.target.scrollTop + 10 >=
            e.target.scrollHeight - e.target.clientHeight
        ) {
            const moreData = filteredObjects.splice(0, 20);

            if (moreData.length > 0) {
                let newData = [...gridData, ...moreData];
                setGridData(newData);
            }
        }
    };

    const dataStateChange = e => {
        const parent = document.getElementById('table');
        let children = parent.getElementsByClassName('k-grid-content k-virtual-content');
        children[0].scrollTop = 0;

        const objs = [...objects];
        const data = orderBy(filterBy(objs, e.dataState.filter), e.dataState.sort);
        const filteredData = data.splice(0, 20);

        setFilteredObjects(data);
        setDataState(e.dataState);
        setGridData(filteredData);
    };

    const onHeaderSelectionChange = (event) => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = cloneDeep(tableSelectState);

        const filteredData = orderBy(filterBy(gridData, dataState.filter), dataState.sort);
        filteredData.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
        });

        updateSelectedState(newSelectedState);
        setTableSelectState(newSelectedState);
    };

    const onSelectionChange = (event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: tableSelectState,
            dataItemKey:   'id',
        });

        updateSelectedState(newSelectedState);
        setTableSelectState(newSelectedState);
    };

    return (
        <div className={classes.tableWrapper} id="table">
            <style>
                { kendoGridInlineStyling }
            </style>
            <Grid
                className={classes.KendoGrid}
                sortable
                dataItemKey="id"
                selectedField="selected"
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
                total={profiles.length}
                data={gridData.map((item) => ({
                    ...item, 'selected': tableSelectState[idGetter(item)],
                }))}
                onScroll={scrollHandler}
                {...dataState}
                onDataStateChange={dataStateChange}
                fixedScroll={true}
                filterable={workbookModeEnabled}
            >
                <Column
                    filterable={false}
                    field="selected"
                    width="75px"
                    headerSelectionValue={
                        orderBy(filterBy(gridData, dataState.filter), dataState.sort).length > 0 && 
                        orderBy(filterBy(gridData, dataState.filter), dataState.sort).findIndex((item) => !tableSelectState[idGetter(item)]) === -1
                    }
                />
                <Column
                    field="name"
                    title="Name"
                    width="auto"
                />
                <Column
                    field="basType"
                    title="Agent Driver"
                    width={350}
                />
                <Column
                    field="profileType"
                    title="Profile Type"
                    width={200}
                />
                <Column
                    field=""
                    title=""
                    cell={actionsCell}
                    locked={true}
                    sortable={false}
                    filterable={false}
                    width={75}
                />
            </Grid>
        </div>);
};

ProfilesTable.defaultProps = {
    isFullProfileAllowed: false,
    onDeleteModal:        () => {},
    onChangeTrendModal:   () => {},
    onSort:               () => {},
    onItemChange:         () => {},
    onItemToggle:         () => {}
};

ProfilesTable.propTypes = {
    companyInfo:          object,
    isFullProfileAllowed: bool,
    onDeleteModal:        func,
    onChangeTrendModal:   func,
    onSort:               func,
    onItemChange:         func,
    onItemToggle:         func,
    profiles:             array.isRequired
};

export default ProfilesTable;
